// Town state computation from answers

export interface Building {
    slot: string;
    type: string;
    level: number;
    x: number;
    y: number;
}

export interface TownState {
    buildings: Building[];
    decorations: Array<{ type: string; x: number; y: number }>;
    tier: 'basic' | 'premium';
}

// Fixed slot positions on the grid (in grid coordinates, not pixels)
const SLOT_POSITIONS: Record<string, { x: number; y: number }> = {
    A: { x: 2, y: 2 },   // Stay type (home/cabin/hotel)
    B: { x: 5, y: 2 },   // Park/Lake (outdoors)
    C: { x: 2, y: 4 },   // Restaurant
    D: { x: 5, y: 4 },   // Activity  
    E: { x: 7, y: 3 },   // Travel station
    F: { x: 0, y: 3 },   // Salon/Beauty
    G: { x: 3, y: 6 },   // Spa
};

const DECORATION_POSITIONS: Record<string, { x: number; y: number }> = {
    music_stage: { x: 4, y: 1 },
    photo_booth: { x: 6, y: 1 },
    wardrobe: { x: 1, y: 1 },
    dessert_cart: { x: 6, y: 5 },
    fountain: { x: 4, y: 3 },
    love_tree: { x: 7, y: 5 },
};

export function computeTownState(answers: Record<string, string>): TownState {
    const buildings: Building[] = [];
    const decorations: Array<{ type: string; x: number; y: number }> = [];

    // Slot A: Home/Cabin/Hotel (stayType)
    const stayType = answers.stayType;
    if (stayType) {
        const stayMap: Record<string, { type: string; level: number }> = {
            home: { type: 'house', level: 1 },
            airbnb: { type: 'cabin', level: 2 },
            hotel: { type: 'hotel', level: 3 },
            'day-only': { type: 'park', level: 1 },
        };
        const stay = stayMap[stayType] || { type: 'house', level: 1 };
        buildings.push({
            slot: 'A',
            type: stay.type,
            level: stay.level,
            ...SLOT_POSITIONS.A,
        });
    }

    // Slot B: Park/Lake (vibe)
    const vibe = answers.vibe;
    if (vibe === 'outdoors') {
        buildings.push({
            slot: 'B',
            type: 'lake',
            level: 1,
            ...SLOT_POSITIONS.B,
        });
    } else if (vibe === 'mix') {
        buildings.push({
            slot: 'B',
            type: 'park',
            level: 1,
            ...SLOT_POSITIONS.B,
        });
    }

    // Slot C: Restaurant (food)
    const food = answers.food;
    if (food) {
        const level = food === 'continental' || food === 'sushi' ? 2 : 1;
        buildings.push({
            slot: 'C',
            type: 'restaurant',
            level,
            ...SLOT_POSITIONS.C,
        });
    }

    // Slot D: Activity building
    const activity = answers.activity;
    if (activity) {
        const activityMap: Record<string, string> = {
            cinema: 'cinema',
            arcade: 'arcade',
            bowling: 'bowling',
            'paint&sip': 'park',
            beach: 'beach',
            'games night': 'house',
        };
        buildings.push({
            slot: 'D',
            type: activityMap[activity] || 'park',
            level: 1,
            ...SLOT_POSITIONS.D,
        });
    }

    // Slot E: Travel station
    if (answers.location === 'travel') {
        buildings.push({
            slot: 'E',
            type: 'station',
            level: answers.travelPlace ? 2 : 1,
            ...SLOT_POSITIONS.E,
        });
    }

    // Slot F: Salon/Beauty
    const hasNails = answers.beautyNails === 'yes';
    const hasLashes = answers.beautyLashes === 'yes';
    if (hasNails || hasLashes) {
        buildings.push({
            slot: 'F',
            type: hasNails && hasLashes ? 'beauty' : hasNails ? 'salon' : 'beauty',
            level: hasNails && hasLashes ? 2 : 1,
            ...SLOT_POSITIONS.F,
        });
    }

    // Slot G: Spa
    if (answers.spaAddon === 'massage' || answers.spaAddon === 'facial') {
        buildings.push({
            slot: 'G',
            type: 'spa',
            level: answers.spaAddon === 'massage' ? 2 : 1,
            ...SLOT_POSITIONS.G,
        });
    }

    // Decorations
    if (answers.music) {
        decorations.push({ type: 'music_stage', ...DECORATION_POSITIONS.music_stage });
    }

    if (answers.photoMode === 'take pics') {
        decorations.push({ type: 'photo_booth', ...DECORATION_POSITIONS.photo_booth });
    }

    if (answers.outfitTheme) {
        decorations.push({ type: 'wardrobe', ...DECORATION_POSITIONS.wardrobe });
    }

    if (answers.dessert && answers.dessert !== 'no') {
        decorations.push({ type: 'dessert_cart', ...DECORATION_POSITIONS.dessert_cart });
    }

    // Compute tier
    const tier: 'basic' | 'premium' =
        food === 'continental' || food === 'sushi' || stayType === 'hotel'
            ? 'premium'
            : 'basic';

    // Premium decorations
    if (tier === 'premium') {
        decorations.push({ type: 'fountain', ...DECORATION_POSITIONS.fountain });
        decorations.push({ type: 'love_tree', ...DECORATION_POSITIONS.love_tree });
    }

    return { buildings, decorations, tier };
}

export function getBuildingAtSlot(townState: TownState, slot: string): Building | undefined {
    return townState.buildings.find((b) => b.slot === slot);
}

export function getTotalBuildingCount(townState: TownState): number {
    return townState.buildings.length + townState.decorations.length;
}
